import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  colors?: string[];
}

interface ProductCardProps {
  product: Product;
  viewMode: 'grid' | 'list';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, viewMode }) => {
  const { addItem } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [colorError, setColorError] = useState('');


  const hasColors = Array.isArray(product.colors) && product.colors.length > 0;

  React.useEffect(() => {
    if (!hasColors) {
      setSelectedColor('');
      return;
    }
    if (selectedColor && !product.colors?.includes(selectedColor)) {
      setSelectedColor('');
    }
  }, [hasColors, product.colors, selectedColor]);

  const getImageUrl = (imagePath: string) => {
    if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
    }
    
    // If it's already a data URL or external URL, return as is
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // Handle file paths - try direct uploads path first
    const normalizedPath = imagePath.replace(/\\/g, '/');
    const filename = normalizedPath.split('/').pop();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const directUrl = `${apiUrl}/uploads/products/${filename}`;
    
    return directUrl;
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasColors && !selectedColor) {
      setColorError('اختر اللون أولاً');
      return;
    }
    setColorError('');
    
    setIsAddingToCart(true);
    
    addItem({
      id: product._id,
      name: product.name,
      price: product.price,
      image: getImageUrl(product.images[0]),
      selectedColor: hasColors ? selectedColor : undefined
    });
    
    // Reset animation after 1.5 seconds
    setTimeout(() => {
      setIsAddingToCart(false);
    }, 1500);
  };

  // Wishlist disabled

  if (viewMode === 'list') {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6 hover:shadow-md transition-all duration-300">
        <div className="flex space-x-6">
          <div className="flex-shrink-0 relative">
            <img
              src={getImageUrl(product.images[0])}
              alt={product.name}
              className="w-32 h-32 object-cover rounded-lg"
              onError={(e) => {
                // Only try fallback once, then use placeholder
                if (!e.currentTarget.src.includes('placeholder')) {
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                  const filename = product.images[0]?.split('/').pop();
                  if (!filename || filename === 'undefined' || filename === 'null') {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                    return;
                  }
                  const fallbackUrl = `${apiUrl}/api/products/image/${filename}`;
                  
                  if (e.currentTarget.src !== fallbackUrl) {
                    e.currentTarget.src = fallbackUrl;
                  } else {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                  }
                } else {
                  // Already tried fallback, use placeholder
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                }
              }}
            />
            {/* Wishlist disabled */}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text truncate transition-colors duration-300">
                <Link to={`/product/${product._id}`} className="hover:text-primary-600 dark:hover:text-primary-400">
                  {product.name}
                </Link>
              </h3>
                             <p className="text-2xl font-bold text-pink-600 dark:text-pink-400 transition-colors duration-300">₪{product.price}</p>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-dark-textSecondary line-clamp-2 transition-colors duration-300">{product.description}</p>
            {hasColors && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
                {product.colors?.map((color) => {
                  const isSelected = selectedColor === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedColor(color);
                        setColorError('');
                      }}
                      className={`h-3 w-3 sm:h-6 sm:w-6 min-h-0 min-w-0 p-0 rounded-full border transition-all ${isSelected ? 'border-pink-500 scale-105 sm:scale-110' : 'border-gray-300 dark:border-dark-border'}`}
                      style={{ backgroundColor: color }}
                      aria-label={`اختر اللون ${color}`}
                      title={color}
                    />
                  );
                })}
              </div>
            )}
            {colorError && <p className="mt-1 text-xs text-red-500">{colorError}</p>}
            <div className="mt-4 flex items-center justify-between">

              <div className="flex items-center space-x-2">
                          <button
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            className={`btn-primary flex items-center space-x-2 transition-all duration-300 ${
              isAddingToCart ? 'bg-green-600 hover:bg-green-700' : ''
            }`}
          >
            {isAddingToCart ? (
              <>
                <Check className="h-4 w-4 animate-bounce" />
                <span>تمت الإضافة!</span>
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4" />
                <span>إضافة إلى السلة</span>
              </>
            )}
          </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden hover:shadow-md transition-all duration-300">
      <div className="relative">
        <Link to={`/product/${product._id}`}>
          <div className="aspect-[3/4] w-full overflow-hidden bg-pink-100 dark:bg-dark-hover">
            <img
              src={getImageUrl(product.images[0])}
              alt={product.name}
              className="h-full w-full object-cover hover:scale-105 transition-transform duration-200 bg-pink-100 dark:bg-dark-hover"
              onError={(e) => {
                // Only try fallback once, then use placeholder
                if (!e.currentTarget.src.includes('placeholder')) {
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                  const filename = product.images[0]?.split('/').pop();
                  if (!filename || filename === 'undefined' || filename === 'null') {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                    return;
                  }
                  const fallbackUrl = `${apiUrl}/api/products/image/${filename}`;
                  
                  if (e.currentTarget.src !== fallbackUrl) {
                    e.currentTarget.src = fallbackUrl;
                  } else {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                  }
                } else {
                  // Already tried fallback, use placeholder
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                }
              }}
            />
          </div>
        </Link>
        
        {/* Wishlist disabled */}
      </div>
      
      <div className="p-3 sm:p-4 flex flex-1 flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500 dark:text-dark-textSecondary uppercase tracking-wide transition-colors duration-300">{product.category}</span>
        </div>
        <h3 className="text-sm sm:text-lg lg:text-lg xl:text-xl font-medium text-gray-900 dark:text-dark-text mb-2 line-clamp-2 transition-colors duration-300">
          <Link to={`/product/${product._id}`} className="hover:text-primary-600 dark:hover:text-primary-400">
            {product.name}
          </Link>
        </h3>
        <p className="text-gray-600 dark:text-dark-textSecondary text-xs sm:text-sm lg:text-sm xl:text-base mb-3 line-clamp-2 min-h-[2.8rem] transition-colors duration-300">{product.description}</p>
        {hasColors && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
            {product.colors?.map((color) => {
              const isSelected = selectedColor === color;
              return (
                <button
                  key={color}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedColor(color);
                    setColorError('');
                  }}
                  className={`h-3 w-3 sm:h-6 sm:w-6 min-h-0 min-w-0 p-0 rounded-full border transition-all ${isSelected ? 'border-pink-500 scale-105 sm:scale-110' : 'border-gray-300 dark:border-dark-border'}`}
                  style={{ backgroundColor: color }}
                  aria-label={`اختر اللون ${color}`}
                  title={color}
                />
              );
            })}
          </div>
        )}
        {colorError && <p className="mb-2 text-xs text-red-500">{colorError}</p>}
        <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <span className="text-lg sm:text-2xl lg:text-2xl xl:text-3xl font-bold text-pink-600 dark:text-pink-400 transition-colors duration-300">₪{product.price}</span>
          <button
            onClick={handleAddToCart}
            disabled={isAddingToCart}
            className={`flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm lg:text-sm xl:text-base py-2 px-3 sm:px-4 lg:py-2 lg:px-4 xl:py-3 xl:px-6 rounded-lg text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-md hover:shadow-lg transition-all duration-300 ${
              isAddingToCart ? 'from-green-600 to-green-700 hover:from-green-600 hover:to-green-700' : ''
            }`}
          >
            {isAddingToCart ? (
              <>
                <Check className="h-4 w-4 animate-bounce" />
                <span>تمت الإضافة!</span>
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4" />
                <span>إضافة إلى السلة</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
